/**
 * ORT-Web session loading.
 *
 * We prefer the WebGPU EP when available (much faster, fits big models on
 * desktop GPUs) and fall back to WASM (works everywhere). Sessions are cached
 * per (component, variant); switching the variant disposes the old session.
 */
import * as ort from "onnxruntime-web";
import { type Variant, type ModelUrlOpts, modelUrl } from "./components";
import { take } from "./model-cache";

// ORT 1.20's bundled entry tries to dynamically import worker .mjs files from
// `./ort/...` relative to itself -- that subfolder doesn't exist in the npm
// package, so the default fetch 404s. Override `wasmPaths` to wherever the
// ORT-Web JSEP worker assets actually live:
//   - prod: VITE_WASM_BASE_URL points at the public R2 host (e.g.
//     https://data.splat.sqrm.co/wasm/), which serves them via R2's CDN.
//   - dev: defaults to /wasm/ which Vite serves from web/public/wasm/ (a
//     symlink into node_modules/onnxruntime-web/dist).
// The wasm file is ~25 MB -- well over Cloudflare Workers' 25 MiB per-asset
// cap, which is the original reason we serve it from R2 rather than bundling.
{
  const wasmBase = (import.meta as any).env?.VITE_WASM_BASE_URL ?? "/wasm/";
  ort.env.wasm.wasmPaths = wasmBase.endsWith("/") ? wasmBase : wasmBase + "/";
}
ort.env.wasm.numThreads = Math.min(4, (navigator.hardwareConcurrency || 4));
ort.env.wasm.simd = true;

export type ComponentId =
  | "rmbg" | "dinov3" | "vae_encoder" | "flow" | "octree_step" | "gs_decoder";

// Components that don't fit in the 4 GB WASM heap on their own. If WebGPU
// isn't engaged for these, the session creation will std::bad_alloc -- fail
// fast with a clear message instead.
const NEEDS_WEBGPU: ReadonlySet<ComponentId> = new Set(["dinov3", "flow"]);

// The pre-acquired device. gpu-loop.ts borrows it to allocate the persistent
// buffers it pins across the flow sampling loop, and to dispatch the WGSL
// Euler kernel.
let webgpuDevice: GPUDevice | null = null;
/** Returns the WebGPU device ORT-Web is currently using. We try
 *  `ort.env.webgpu.device` first because ORT may swap its internal device
 *  out from under us after a device-loss event or session re-init; our
 *  cached `webgpuDevice` would then be stale and any buffer allocated
 *  against it would fail with "Buffer X is associated with Device,
 *  and cannot be used with Device". */
export function getWebgpuDevice(): GPUDevice | null {
  const ortDev = (ort.env.webgpu as any)?.device;
  if (ortDev) webgpuDevice = ortDev;
  return webgpuDevice;
}

/** IEEE 754 binary16 (Uint16Array) -> binary32. Subnormals decoded with
 *  the standard 2^-14 * frac/1024 formula; +/-Inf and NaN preserved. */
export function fp16ToFp32(src: Uint16Array): Float32Array {
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) {
    const h = src[i];
    const sign = (h & 0x8000) >>> 15;
    const exp  = (h & 0x7c00) >>> 10;
    const frac = h & 0x03ff;
    let v: number;
    if (exp === 0) {
      v = frac === 0 ? 0 : Math.pow(2, -14) * (frac / 1024);
    } else if (exp === 31) {
      v = frac === 0 ? Infinity : NaN;
    } else {
      v = Math.pow(2, exp - 15) * (1 + frac / 1024);
    }
    out[i] = sign ? -v : v;
  }
  return out;
}

/** Return an `ort.Tensor`'s underlying values as a Float32Array regardless
 *  of whether the model produced fp32 or fp16. Pass the result to any JS-side
 *  math (Euler step, CFG blend, softmax, gaussian unpack) so we never accidentally
 *  reinterpret fp16 bit patterns as fp32 floats -- a bug that quietly turns
 *  every downstream gaussian into garbage when an fp16/int4 variant is loaded. */
export function tensorFloat32(t: ort.Tensor): Float32Array {
  if (t.type === "float32") return t.data as Float32Array;
  if (t.type === "float16") return fp16ToFp32(t.data as Uint16Array);
  throw new Error(`tensorFloat32: unsupported dtype ${t.type}`);
}

/** Float32 -> Float16 (IEEE 754 binary16, packed as Uint16Array -- the only
 *  storage form ORT.Tensor accepts for "float16" tensors).
 *  Handles subnormals as 0 and overflow as +/-Inf; preserves NaN. */
export function fp32ToFp16(src: Float32Array): Uint16Array {
  const out = new Uint16Array(src.length);
  const f32 = new Float32Array(1);
  const u32 = new Uint32Array(f32.buffer);
  for (let i = 0; i < src.length; i++) {
    f32[0] = src[i];
    const x = u32[0];
    const sign = (x >>> 16) & 0x8000;
    const expRaw = (x >>> 23) & 0xff;
    const mantissa = x & 0x7fffff;
    if (expRaw === 0xff) {
      out[i] = sign | 0x7c00 | (mantissa ? 0x200 : 0);
      continue;
    }
    let exp = expRaw - 127 + 15;
    if (exp >= 31) { out[i] = sign | 0x7c00; continue; }      // overflow -> Inf
    if (exp <= 0)  { out[i] = sign; continue; }                // underflow -> 0
    out[i] = sign | (exp << 10) | (mantissa >>> 13);
  }
  return out;
}

/** Recast `tensor` to whatever dtype `session` expects for `inputName`.
 *  Currently handles the fp32 <-> fp16 boundary that shows up when mixing
 *  variants (e.g. flow output is fp32 but octree_fp16 expects fp16 cond).
 *  Returns the original tensor if the dtype already matches. */
export function adaptInput(
  session: ort.InferenceSession, inputName: string, tensor: ort.Tensor,
): ort.Tensor {
  const meta = session.inputMetadata.find(
    (m): m is ort.InferenceSession.TensorValueMetadata =>
      m.isTensor && m.name === inputName);
  if (!meta || meta.type === tensor.type) return tensor;
  if (tensor.type === "float32" && meta.type === "float16") {
    return new ort.Tensor("float16", fp32ToFp16(tensor.data as Float32Array), tensor.dims);
  }
  throw new Error(
    `dtype mismatch on '${inputName}': model wants ${meta.type}, ` +
    `we have ${tensor.type}; no automatic cast available`
  );
}

/** Wrap a session.run with two safety nets:
 *  1. adaptInput() each feed to the session's expected dtype, so mixing
 *     fp16 / fp32 model variants doesn't error out at the boundary.
 *  2. check the __webgpuFatalError flag the uncapturederror listener sets
 *     and bail before queueing more work on a dead device.
 *
 *  We deliberately don't pushErrorScope here -- nested scopes have to
 *  pop in LIFO order, which would force us to serialize every safeRun
 *  call. The uncapturederror listener already gives us fail-fast on the
 *  next call, which is fine. */
export async function safeRun(
  sess: ort.InferenceSession,
  feeds: Record<string, ort.Tensor>,
): Promise<ort.InferenceSession.OnnxValueMapType> {
  for (const [name, t] of Object.entries(feeds)) {
    feeds[name] = adaptInput(sess, name, t);
  }
  const pending = (globalThis as any).__webgpuFatalError;
  if (pending) throw new Error(`WebGPU device in error state: ${pending}`);
  return sess.run(feeds);
}

let webgpuSupported: boolean | null = null;
async function detectWebGPU(): Promise<boolean> {
  if (webgpuSupported !== null) return webgpuSupported;
  if (!("gpu" in navigator)) { webgpuSupported = false; return false; }
  try {
    const adapter = await (navigator as any).gpu.requestAdapter();
    if (!adapter) { webgpuSupported = false; return false; }

    const lim = adapter.limits;
    const maxBuf = lim.maxStorageBufferBindingSize;
    const maxStorage = lim.maxStorageBuffersPerShaderStage;
    console.log(`[webgpu] adapter maxStorageBufferBindingSize = ${(maxBuf/1e6).toFixed(0)} MB, ` +
                `maxStorageBuffersPerShaderStage = ${maxStorage}`);

    // Pre-acquire the device with the adapter's MAX limits so ORT-Web's
    // WebGPU EP doesn't fall back to the spec minimums (which are 8 storage
    // buffers per stage, way too few for a Concat with >7 inputs). Hand
    // the device to ORT via env.webgpu.device.
    const requiredLimits: Record<string, number> = {};
    const wanted = [
      "maxBufferSize", "maxStorageBufferBindingSize",
      "maxStorageBuffersPerShaderStage", "maxStorageTexturesPerShaderStage",
      "maxComputeWorkgroupStorageSize",
      "maxComputeInvocationsPerWorkgroup", "maxComputeWorkgroupSizeX",
      "maxComputeWorkgroupSizeY", "maxComputeWorkgroupSizeZ",
      "maxComputeWorkgroupsPerDimension",
    ];
    for (const k of wanted) {
      const v = (lim as any)[k];
      if (typeof v === "number") requiredLimits[k] = v;
    }
    const device = await adapter.requestDevice({ requiredLimits });
    webgpuDevice = device;
    (ort.env.webgpu as any).device = device;
    // Surface unrecoverable WebGPU errors immediately. Without this, ORT-Web
    // keeps queueing work onto a device that's already in error state and
    // we get a cascade of 'Invalid CommandBuffer'.
    device.addEventListener("uncapturederror", (ev: any) => {
      const err = ev.error || ev;
      console.error("[webgpu] uncaptured device error -- generation will abort:",
                    err.message || err);
      (globalThis as any).__webgpuFatalError = err.message || String(err);
    });
    console.log(`[webgpu] device pre-acquired at adapter max limits ` +
                `(${requiredLimits.maxStorageBuffersPerShaderStage} storage bufs/stage)`);

    webgpuSupported = true;
  } catch (e) {
    console.warn("[webgpu] adapter/device acquisition failed:", e);
    webgpuSupported = false;
  }
  return webgpuSupported;
}

export async function loadComponent(id: ComponentId, variant: Variant,
                                    urlOpts?: ModelUrlOpts) {
  const url = modelUrl(id, variant, urlOpts);
  const useWebGPU = await detectWebGPU();
  if (NEEDS_WEBGPU.has(id) && !useWebGPU) {
    throw new Error(
      `${id} needs WebGPU -- your browser doesn't expose navigator.gpu (or it's ` +
      `gated by a flag). WASM-only would OOM on a model this size. ` +
      `Try Chrome with chrome://flags/#enable-unsafe-webgpu enabled, ` +
      `or pick a smaller variant of this component.`
    );
  }

  // Get the .onnx bytes from the shared model cache. `take` reuses the
  // prefetch's in-flight Promise if there is one (no double-download), or
  // initiates a fresh fetch otherwise. Skips the magic-byte HTML sniff that
  // used to be a separate Range request -- we have the full body now, so we
  // can inspect the first byte directly.
  const onnxBytes = await take(url);
  const headByte = new Uint8Array(onnxBytes, 0, 1)[0];
  if (headByte === 0x3c || headByte === 0xef) {
    throw new Error(
      `${url} returned HTML, not ONNX bytes -- this variant was probably never produced. ` +
      `Run \`python onnx/quantize.py\` on the export host first, then refresh.`
    );
  }

  // External data sidecar -- some variants don't have one (rmbg_int8,
  // gs_decoder_fp32). `take` rejects with a 404 in that case; we swallow the
  // error and proceed without externalData.
  const dataUrl = url + ".data";
  let externalData: ort.InferenceSession.SessionOptions["externalData"];
  let dataBytes: ArrayBuffer | undefined;
  try {
    dataBytes = await take(dataUrl);
    const fileName = url.split("/").pop()! + ".data";
    externalData = [{ data: new Uint8Array(dataBytes), path: fileName }];
  } catch { /* no sidecar */ }

  // The chunked flow export has a tree-of-binary-Concats topology that ORT's
  // optimizer would fuse back into >7-input Concats, blowing the WebGPU
  // storage-buffer-per-stage cap. So we disable optimization for the chunked
  // flow.
  //
  // The flash flow export is a single SDPA per attention block -- exactly
  // the pattern the ORT WebGPU optimizer needs to fuse into
  // com.microsoft.MultiHeadAttention (the tiled flash kernel). Letting the
  // optimizer run at "all" is what unlocks the speedup, so we re-enable it
  // for the flash variant.
  const isFlashFlow = id === "flow" && urlOpts?.flash;
  const optLevel: ort.InferenceSession.SessionOptions["graphOptimizationLevel"] =
    (id === "flow" && !isFlashFlow) ? "disabled" : "all";

  const opts: ort.InferenceSession.SessionOptions = {
    executionProviders: useWebGPU ? ["webgpu", "wasm"] : ["wasm"],
    graphOptimizationLevel: optLevel,
    // 2 = warning. flow used to be verbose (0) while we hunted the
    // Concat-fusion bug; now that .clone() barriers in the export keep
    // everything binary, verbose just spams the console.
    logSeverityLevel: 3,
    ...(externalData ? { externalData } : {}),
  };

  // Hand the bytes directly to ORT -- no second fetch. ORT copies into the
  // WASM heap, so our `onnxBytes`/`dataBytes` references can be GC'd once
  // session.create resolves.
  const t0 = performance.now();
  let sess: ort.InferenceSession;
  try {
    sess = await ort.InferenceSession.create(new Uint8Array(onnxBytes), opts);
  } catch (e) {
    const msg = (e as Error)?.message ?? String(e);
    if (msg.includes("bad_alloc") || msg.includes("memory")) {
      throw new Error(
        `Out of memory loading ${id} (${variant}). The WebAssembly heap is ` +
        `capped at 4 GB; this model needs more during parse. Pick a smaller ` +
        `variant (int4 if available), or launch Chrome with ` +
        `--enable-features=WebAssemblyMemory64. Original: ${msg}`
      );
    }
    throw e;
  }
  const dt = ((performance.now() - t0) / 1000).toFixed(1);
  const tag = isFlashFlow ? `${variant}+flash` : variant;
  console.log(`[ort] ${id} (${tag}) session ready in ${dt}s -- EP wanted: ` +
              `${opts.executionProviders?.join(",")}`);
  return sess;
}
